<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant } from '@/composables/useTenant';
import { INITIAL_PLAYERS } from '@/services/mockData';
import type { PlayerProfile } from '@/types/models';
import AppBadge from '@/components/ui/AppBadge.vue';
import AppModal from '@/components/ui/AppModal.vue';
import { useToastNotification } from '@/composables/useToastNotification';
import { NButton, NInput, NTag } from 'naive-ui';
import {
  GraduationCap,
  Flame,
  Search,
  Download,
  Copy,
  TrendingUp,
  School,
  CheckCircle2,
} from 'lucide-vue-next';

const { t } = useI18n();
const { activeTenant } = useTenant();
const { notifySuccess, notifyInfo } = useToastNotification();

export interface ProspectProfile extends PlayerProfile {
  gpa: number;
  ncaaId: string;
  sixtyYard: number;
  popTime?: number;
  exitVeloMax: number;
  armVeloMax: number;
  commitmentStatus: 'UNCOMMITTED' | 'COMMITTED' | 'TRANSFER_PORTAL';
  committedSchool?: string;
  interestedColleges: string[];
}

const prospects = ref<ProspectProfile[]>([
  {
    ...INITIAL_PLAYERS[0], // 佐藤 翔太 P #18
    gpa: 3.82,
    ncaaId: '2409817429',
    sixtyYard: 6.64,
    exitVeloMax: 94.4,
    armVeloMax: 95.8, // 154 km/h
    commitmentStatus: 'COMMITTED',
    committedSchool: '慶應義塾大学体育会野球部 (東京六大学)',
    interestedColleges: ['慶應義塾大', '早稲田大', '明治大', '東京ガス (社会人)'],
  },
  {
    ...INITIAL_PLAYERS[1], // 山田 雄大 SS #24
    gpa: 3.65,
    ncaaId: '2501928471',
    sixtyYard: 6.38,
    exitVeloMax: 96.2,
    armVeloMax: 90.5,
    commitmentStatus: 'COMMITTED',
    committedSchool: 'ENEOS野球部 (都市対抗・社会人)',
    interestedColleges: ['ENEOS', 'トヨタ自動車', '法政大', '立教大'],
  },
  {
    ...INITIAL_PLAYERS[2], // 中村 拓海 CF #7
    gpa: 3.90,
    ncaaId: '2604819203',
    sixtyYard: 6.52,
    exitVeloMax: 96.5,
    armVeloMax: 88.4,
    commitmentStatus: 'UNCOMMITTED',
    interestedColleges: ['早稲田大', '青山学院大', '中央大', '日本通運'],
  },
  {
    ...INITIAL_PLAYERS[3], // 小林 蓮 C #10
    gpa: 3.45,
    ncaaId: '2508192731',
    sixtyYard: 7.05,
    popTime: 1.88,
    exitVeloMax: 98.1,
    armVeloMax: 86.5,
    commitmentStatus: 'COMMITTED',
    committedSchool: '明治大学硬式野球部 (東京六大学)',
    interestedColleges: ['明治大', '東洋大', '亜細亜大'],
  },
  {
    ...INITIAL_PLAYERS[4], // 渡辺 隼人 2B #1
    gpa: 3.75,
    ncaaId: '2701829471',
    sixtyYard: 6.68,
    exitVeloMax: 91.0,
    armVeloMax: 85.2,
    commitmentStatus: 'UNCOMMITTED',
    interestedColleges: ['青山学院大', '國學院大', '駒澤大'],
  },
  {
    ...INITIAL_PLAYERS[5], // 伊藤 陸 3B #5
    gpa: 3.55,
    ncaaId: '2509182741',
    sixtyYard: 6.92,
    exitVeloMax: 97.4,
    armVeloMax: 89.0,
    commitmentStatus: 'UNCOMMITTED',
    interestedColleges: ['日本通運', 'NTT東日本', '法政大'],
  },
]);

const searchQuery = ref('');
const gradYearFilter = ref<string>('ALL');
const statusFilter = ref<string>('ALL');
const selectedProspect = ref<ProspectProfile | null>(null);
const isScoutModalOpen = ref(false);

const filteredProspects = computed(() => {
  return prospects.value.filter(p => {
    if (gradYearFilter.value !== 'ALL' && String(p.graduationYear) !== gradYearFilter.value) return false;
    if (statusFilter.value !== 'ALL' && p.commitmentStatus !== statusFilter.value) return false;
    const q = searchQuery.value.toLowerCase();
    const fullName = `${p.lastName} ${p.firstName}`.toLowerCase();
    return fullName.includes(q) || p.position.toLowerCase().includes(q) || (p.committedSchool || '').toLowerCase().includes(q);
  });
});

const openProspectDetail = (prospect: ProspectProfile) => {
  selectedProspect.value = prospect;
  isScoutModalOpen.value = true;
};

const copyScoutPacketLink = (prospect: ProspectProfile) => {
  const url = `https://scout.nextbaseteamcloud.com/prospect/${prospect.ncaaId}`;
  navigator.clipboard.writeText(url);
  notifySuccess(t('recruitingHub.scoutLinkCopied'), t('recruitingHub.scoutLinkCopiedMsg', { name: `${prospect.lastName} ${prospect.firstName}` }));
};

const downloadScoutPdf = (prospect: ProspectProfile) => {
  notifyInfo(t('recruitingHub.generatingScoutPacket'), t('recruitingHub.generatingScoutPacketMsg', { num: prospect.jerseyNumber, name: prospect.lastName }));
};
</script>

<template>
  <div class="space-y-6 animate-in fade-in duration-200">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-800/80">
      <div>
        <div class="flex items-center gap-2">
          <h1 class="text-2xl sm:text-3xl font-extrabold text-slate-100 tracking-tight">
            {{ t('recruitingHub.title') }}
          </h1>
          <AppBadge variant="accent">{{ t('recruitingHub.verifiedBadge') }}</AppBadge>
        </div>
        <p class="mt-1 text-xs sm:text-sm text-slate-400">
          {{ t('recruitingHub.subtitle', { name: activeTenant.name }) }}
        </p>
      </div>

      <div class="flex items-center gap-3">
        <div class="px-3.5 py-1.5 rounded-xl bg-slate-900 border border-slate-800 flex items-center gap-2 text-xs">
          <GraduationCap class="w-4 h-4 text-sky-400" />
          <span class="text-slate-300 font-semibold">{{ t('recruitingHub.commitsBadge', { count: prospects.filter(p => p.commitmentStatus === 'COMMITTED').length }) }}</span>
        </div>
      </div>
    </div>

    <!-- Quick Stats Banner -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div class="p-4 bg-slate-900 border border-slate-800 rounded-2xl flex items-center justify-between">
        <div>
          <span class="text-[10px] text-slate-400 uppercase font-bold tracking-wider block">{{ t('recruitingHub.topExitVelo') }}</span>
          <span class="text-2xl font-mono font-black text-amber-400 mt-0.5 block">98.1 MPH</span>
          <span class="text-[11px] text-slate-500">小林 蓮 (2025年卒)</span>
        </div>
        <div class="w-10 h-10 rounded-xl bg-amber-500/15 text-amber-400 flex items-center justify-center">
          <Flame class="w-5 h-5" />
        </div>
      </div>

      <div class="p-4 bg-slate-900 border border-slate-800 rounded-2xl flex items-center justify-between">
        <div>
          <span class="text-[10px] text-slate-400 uppercase font-bold tracking-wider block">{{ t('recruitingHub.fastest60') }}</span>
          <span class="text-2xl font-mono font-black text-emerald-400 mt-0.5 block">6.38 s</span>
          <span class="text-[11px] text-slate-500">山田 雄大 (2025年卒)</span>
        </div>
        <div class="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center">
          <TrendingUp class="w-5 h-5" />
        </div>
      </div>

      <div class="p-4 bg-slate-900 border border-slate-800 rounded-2xl flex items-center justify-between">
        <div>
          <span class="text-[10px] text-slate-400 uppercase font-bold tracking-wider block">{{ t('recruitingHub.avgGpa') }}</span>
          <span class="text-2xl font-mono font-black text-sky-400 mt-0.5 block">3.69 GPA</span>
          <span class="text-[11px] text-slate-500">文武両道基準達成 (全選手)</span>
        </div>
        <div class="w-10 h-10 rounded-xl bg-sky-500/15 text-sky-400 flex items-center justify-center">
          <School class="w-5 h-5" />
        </div>
      </div>
    </div>

    <!-- Filters & Search -->
    <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
      <div class="relative max-w-sm flex-1">
        <NInput
          v-model:value="searchQuery"
          :placeholder="t('recruitingHub.searchPlaceholder')"
          clearable
          size="medium"
        >
          <template #prefix>
            <Search class="w-4 h-4 text-slate-400" />
          </template>
        </NInput>
      </div>

      <div class="flex items-center gap-2">
        <select
          v-model="gradYearFilter"
          class="bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-sky-500"
        >
          <option value="ALL">{{ t('recruitingHub.allGradClasses') }}</option>
          <option value="2025">{{ t('recruitingHub.class2025') }}</option>
          <option value="2026">{{ t('recruitingHub.class2026') }}</option>
          <option value="2027">{{ t('recruitingHub.class2027') }}</option>
        </select>

        <select
          v-model="statusFilter"
          class="bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-sky-500"
        >
          <option value="ALL">{{ t('recruitingHub.allStatuses') }}</option>
          <option value="UNCOMMITTED">{{ t('recruitingHub.uncommittedOnly') }}</option>
          <option value="COMMITTED">{{ t('recruitingHub.committedOnly') }}</option>
        </select>
      </div>
    </div>

    <!-- Prospects Roster Showcase Table -->
    <div class="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse text-xs">
          <thead>
            <tr class="border-b border-slate-800/80 bg-slate-950/40 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              <th class="py-3.5 px-4 text-center w-14">#</th>
              <th class="py-3.5 px-4">{{ t('recruitingHub.colProspect') }}</th>
              <th class="py-3.5 px-4">{{ t('recruitingHub.colClass') }}</th>
              <th class="py-3.5 px-4">{{ t('recruitingHub.colExitVelo') }}</th>
              <th class="py-3.5 px-4">{{ t('recruitingHub.colArmVelo') }}</th>
              <th class="py-3.5 px-4">{{ t('recruitingHub.colSixty') }}</th>
              <th class="py-3.5 px-4">{{ t('recruitingHub.colGpa') }}</th>
              <th class="py-3.5 px-4">{{ t('recruitingHub.colStatusSchool') }}</th>
              <th class="py-3.5 px-4 text-right">{{ t('recruitingHub.colScoutPacket') }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-800/60 font-sans">
            <tr
              v-for="prospect in filteredProspects"
              :key="prospect.id"
              @click="openProspectDetail(prospect)"
              class="hover:bg-slate-800/40 transition group cursor-pointer"
            >
              <td class="py-3.5 px-4 text-center font-mono font-extrabold text-sm text-sky-400">
                #{{ prospect.jerseyNumber }}
              </td>

              <td class="py-3.5 px-4">
                <div class="font-bold text-slate-200 group-hover:text-sky-300 transition flex items-center gap-1.5">
                  <span>{{ prospect.lastName }} {{ prospect.firstName }}</span>
                  <AppBadge variant="position">{{ prospect.position }}</AppBadge>
                </div>
                <div class="text-[10px] text-slate-500 font-mono mt-0.5">
                  ID: {{ prospect.ncaaId }} &bull; {{ prospect.bats }}/{{ prospect.throws }}
                </div>
              </td>

              <td class="py-3.5 px-4 text-slate-300 font-semibold">
                {{ prospect.graduationYear }}
              </td>

              <td class="py-3.5 px-4 font-mono font-bold text-emerald-400">
                {{ prospect.exitVeloMax }} MPH
              </td>

              <td class="py-3.5 px-4 font-mono font-bold text-sky-400">
                {{ prospect.armVeloMax }} MPH
              </td>

              <td class="py-3.5 px-4 font-mono font-bold text-amber-400">
                {{ prospect.sixtyYard }} s
              </td>

              <td class="py-3.5 px-4 font-mono text-slate-300 font-bold">
                {{ prospect.gpa.toFixed(2) }}
              </td>

              <td class="py-3.5 px-4">
                <div v-if="prospect.commitmentStatus === 'COMMITTED'" class="flex items-center gap-1.5 text-emerald-400 font-bold">
                  <CheckCircle2 class="w-3.5 h-3.5 shrink-0" />
                  <span class="truncate max-w-[160px]">{{ prospect.committedSchool }}</span>
                </div>
                <div v-else>
                  <NTag type="info" size="small" round :bordered="false">{{ t('recruitingHub.uncommittedTag') }}</NTag>
                </div>
              </td>

              <td class="py-3.5 px-4 text-right" @click.stop>
                <div class="flex items-center justify-end gap-1.5">
                  <button
                    type="button"
                    @click="copyScoutPacketLink(prospect)"
                    class="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
                    :title="t('recruitingHub.copyLinkTitle')"
                  >
                    <Copy class="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    @click="downloadScoutPdf(prospect)"
                    class="p-1.5 rounded-lg bg-sky-500/15 hover:bg-sky-500/25 text-sky-300 border border-sky-500/30 transition"
                    :title="t('recruitingHub.downloadPdfTitle')"
                  >
                    <Download class="w-3.5 h-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal: Prospect Scouting Dossier -->
    <AppModal
      v-model="isScoutModalOpen"
      :title="t('recruitingHub.dossierTitle')"
      :subtitle="selectedProspect ? `#${selectedProspect.jerseyNumber} ${selectedProspect.lastName} ${selectedProspect.firstName} • ${selectedProspect.graduationYear}年卒` : ''"
      max-width="lg"
    >
      <div v-if="selectedProspect" class="space-y-6">
        <!-- Header Card -->
        <div class="p-4 bg-slate-950 rounded-2xl border border-slate-800 flex items-center justify-between gap-4">
          <div class="flex items-center gap-3">
            <div class="w-14 h-14 rounded-2xl bg-gradient-to-tr from-sky-500 to-indigo-600 flex items-center justify-center text-white font-black text-2xl shadow-lg shadow-sky-500/20">
              #{{ selectedProspect.jerseyNumber }}
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h3 class="text-base font-bold text-slate-100">{{ selectedProspect.lastName }} {{ selectedProspect.firstName }}</h3>
                <AppBadge variant="position">{{ selectedProspect.position }}</AppBadge>
              </div>
              <p class="text-xs text-slate-400 mt-0.5">
                ID: <span class="font-mono text-slate-300 font-bold">{{ selectedProspect.ncaaId }}</span> &bull; GPA: <span class="font-mono text-emerald-400 font-bold">{{ selectedProspect.gpa }}</span>
              </p>
            </div>
          </div>

          <AppBadge :variant="selectedProspect.commitmentStatus === 'COMMITTED' ? 'success' : 'primary'">
            {{ selectedProspect.commitmentStatus }}
          </AppBadge>
        </div>

        <!-- Radar Metrics Grid -->
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div class="p-3 bg-slate-950 rounded-xl border border-slate-800 text-center">
            <span class="text-[10px] uppercase font-bold text-slate-500 block">{{ t('recruitingHub.peakExitVelo') }}</span>
            <span class="text-base font-mono font-extrabold text-amber-400 mt-0.5 block">{{ selectedProspect.exitVeloMax }} MPH</span>
          </div>
          <div class="p-3 bg-slate-950 rounded-xl border border-slate-800 text-center">
            <span class="text-[10px] uppercase font-bold text-slate-500 block">{{ t('recruitingHub.infieldArmVelo') }}</span>
            <span class="text-base font-mono font-extrabold text-sky-400 mt-0.5 block">{{ selectedProspect.armVeloMax }} MPH</span>
          </div>
          <div class="p-3 bg-slate-950 rounded-xl border border-slate-800 text-center">
            <span class="text-[10px] uppercase font-bold text-slate-500 block">{{ t('recruitingHub.sixtyYardDash') }}</span>
            <span class="text-base font-mono font-extrabold text-emerald-400 mt-0.5 block">{{ selectedProspect.sixtyYard }} s</span>
          </div>
          <div class="p-3 bg-slate-950 rounded-xl border border-slate-800 text-center">
            <span class="text-[10px] uppercase font-bold text-slate-500 block">{{ t('recruitingHub.heightWeight') }}</span>
            <span class="text-xs font-bold text-slate-200 mt-1 block">185cm / 84kg</span>
          </div>
        </div>

        <!-- College Recruiting List -->
        <div class="space-y-2">
          <span class="text-xs font-bold uppercase tracking-wider text-slate-400 block">{{ t('recruitingHub.recruitingPipeline') }}</span>
          <div class="p-3.5 bg-slate-950 rounded-2xl border border-slate-800 flex flex-wrap gap-2">
            <span
              v-for="college in selectedProspect.interestedColleges"
              :key="college"
              class="px-2.5 py-1 rounded-xl bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-200"
            >
              {{ college }}
            </span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="pt-3 flex justify-between gap-3 border-t border-slate-800">
          <NButton quaternary @click="isScoutModalOpen = false">{{ t('common.close') }}</NButton>
          <div class="flex items-center gap-2">
            <NButton secondary @click="copyScoutPacketLink(selectedProspect)">
              <template #icon><Copy class="w-3.5 h-3.5" /></template>
              {{ t('recruitingHub.copyRecruiterLink') }}
            </NButton>
            <NButton type="primary" @click="downloadScoutPdf(selectedProspect)" class="font-bold text-xs shadow-md">
              <template #icon><Download class="w-3.5 h-3.5" /></template>
              {{ t('recruitingHub.downloadPdfPacket') }}
            </NButton>
          </div>
        </div>
      </div>
    </AppModal>
  </div>
</template>
